import axios from 'axios';
import {BACKEND_BASEURL} from "@/config/config";

/**
 * Read-only accountability reports (backend /accountability): DATEN_CRMSG
 * delivery history per metering point and the community sums report. The
 * copy/paste report strings for DSO complaints are built here so they are
 * unit-testable.
 */

export async function fetchEnergyDataHistory(ecn, ecid, meteringPoint, dateFrom, dateTo) {
  const response = await axios.get(
    `${BACKEND_BASEURL}accountability/${ecn}/${ecid}/energyDataHistory/${meteringPoint}`
    + `/from/${dateFrom}/to/${dateTo}`,
  );
  return response.data;
}

export async function fetchSumsReport(ecn, ecid, dateFrom, dateTo) {
  const response = await axios.get(
    `${BACKEND_BASEURL}accountability/${ecn}/${ecid}/sumsReport/from/${dateFrom}/to/${dateTo}`,
  );
  return response.data;
}

const iso = (value) => value ? String(value).slice(0, 10) : '-';
export const fmtKwh = (value) => typeof value === 'number' ? Number(value.toFixed(3)) : value;
const isoTime = (value) => value ? new Date(value).toISOString().replace('T', ' ').slice(0, 19) : null;

/** plain-text report of the DATEN_CRMSG history, ready to paste into a DSO mail */
export function buildEnergyDataHistoryReport(history) {
  const lines = [];
  lines.push(`Energiedaten-Historie ZP ${history.meteringPoint} (${history.ecid})`);
  lines.push(`Zeitraum ${iso(history.dateFrom)} bis ${iso(history.dateTo)}`);
  lines.push('');
  lines.push(`DATEN_CRMSG Nachrichten: ${history.messages.length}`);
  for (const m of history.messages) {
    lines.push(`${isoTime(m.arrivalDate)}  ${m.messageId}  ${m.conversationId}  `
      + `Daten ${iso(m.dataRangeFrom)}-${iso(m.dataRangeTo)}  `
      + `verarbeitet: ${isoTime(m.dateProcessed) || 'FEHLT'}`);
  }
  lines.push('');
  lines.push('Updates pro Tag:');
  for (const u of history.updatesPerDay) {
    lines.push(`${u.date}: ${u.count}`);
  }
  lines.push('');
  lines.push('Summen je Zählwerkscode:');
  for (const s of history.sumsByMeterCode) {
    lines.push(`${s.meterCode}: ${fmtKwh(s.energySumkWh)} kWh (Worst-Case ${s.meteringMethod})`);
  }
  return lines.join('\n');
}

/**
 * Sum deviation mail (Summenabweichungen): one table per month with the
 * transmitted energy per DSO and meter code. Billing-relevant codes (per the
 * reconciliation: producer G.01T minus P.01T, consumer G.03) are marked with
 * an asterisk, and the producer difference row is appended per DSO.
 */
const BILLING_RELEVANT_CODES = ['1-1:2.9.0 G.01T', '1-1:2.9.0 P.01T', '1-1:2.9.0 G.03'];
const PRODUCER_FOR_COMMUNITY = '1-1:2.9.0 G.01T';
const PRODUCER_REMAINDER = '1-1:2.9.0 P.01T';

// deterministic de-AT number format (dot thousands, comma decimals, 2 to 3
// fraction digits) — toLocaleString grouping varies between ICU builds
const deAt = (value) => {
  let fixed = value.toFixed(3);
  if (fixed.endsWith('0')) fixed = fixed.slice(0, -1);
  const [intPart, fracPart] = fixed.split('.');
  const sign = intPart.startsWith('-') ? '-' : '';
  const digits = sign ? intPart.slice(1) : intPart;
  const grouped = digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${sign}${grouped},${fracPart}`;
};

export function buildSumsReport(report, communityName) {
  const lines = [];
  lines.push('Hello Team');
  lines.push('Our internal total checks show that the energy quantities allocated for this community differ from each other.');
  lines.push('The amount of energy fed into the grid differs from the amount of energy consumed.');
  lines.push('');
  lines.push('Below you will find a list of the transmitted energy quantities per meter code and month.');
  lines.push('The metercodes relevant to billing are marked with *.');
  lines.push('');
  lines.push('Please check whether the same quantities are stored in your system.');
  lines.push('In case of deviations, I ask that the energy data for the community be sent again.');
  lines.push('Also ask for confirmation if the quantities match your quantities.');
  lines.push('');
  if (communityName) lines.push(communityName);
  lines.push(`ECN: ${report.ecn}`);
  lines.push(`ECID: ${report.ecid}`);

  for (const month of report.months || []) {
    if (!month.dsoSums.length) continue;
    lines.push('');
    lines.push(month.label);
    lines.push('ecnDso\tmeterCode\tMM\ttotalEnergy');

    const sorted = [...month.dsoSums].sort((a, b) =>
      a.ecnDso.localeCompare(b.ecnDso) || a.meterCode.localeCompare(b.meterCode));
    for (const s of sorted) {
      const mark = BILLING_RELEVANT_CODES.includes(s.meterCode) ? ' *' : '';
      lines.push(`${s.ecnDso}\t${s.meterCode}${mark}\t${s.meteringMethod}\t${deAt(s.totalEnergy)}`);
    }

    // producer community-assigned energy per DSO: G.01T minus P.01T
    for (const dso of [...new Set(sorted.map(s => s.ecnDso))]) {
      const g01t = sorted.find(s => s.ecnDso === dso && s.meterCode === PRODUCER_FOR_COMMUNITY);
      const p01t = sorted.find(s => s.ecnDso === dso && s.meterCode === PRODUCER_REMAINDER);
      if (g01t && p01t) {
        lines.push('');
        lines.push(`${dso}\t${PRODUCER_FOR_COMMUNITY} - ${PRODUCER_REMAINDER}\t\t${deAt(g01t.totalEnergy - p01t.totalEnergy)}`);
      }
    }
  }

  lines.push('');
  lines.push('Thank you and best regards');
  return lines.join('\n');
}
